/**
 * Site / location naming from GET /installation-info/ `sites`:
 * sites[] → { name, country, address, image, company, devices[] → { serial_number, hlink_location_name } }.
 * Older Settings fields (hlinks[serial].installation_location) and live device_info.city are fallbacks only.
 */

/** serial → { site, device, company } */
export const buildSiteIndex = (store) => {
  const map = new Map();
  (Array.isArray(store?.sites) ? store.sites : []).forEach((site) => {
    (site?.devices || []).forEach((device) => {
      const serial = String(device?.serial_number || "");
      if (serial) map.set(serial, { site, device, company: site?.company || null });
    });
  });
  return map;
};

export const hlinkSiteName = (serial, stored, info, entry) =>
  entry?.site?.name ||
  stored?.installation_location ||
  stored?.installation_info ||
  info?.city ||
  (serial ? `H-LINK ${serial}` : "");

/** H-LINK physical location from the device API (e.g. "Break Belt"). */
export const hlinkLocationName = (entry) => String(entry?.device?.hlink_location_name || "").trim();

/** Registered device display name (not HL-01 codes, not location). */
export const hlinkDeviceName = (entry) => String(entry?.device?.name || "").trim();

/** "IN" → "India"; non-code text is returned unchanged. */
let regionNames;
const countryNameCache = new Map();

export const countryName = (code) => {
  const text = String(code || "").trim();
  if (!text) return "";
  if (!/^[A-Za-z]{2}$/.test(text)) return text;
  const upper = text.toUpperCase();
  if (countryNameCache.has(upper)) return countryNameCache.get(upper);
  let name;
  try {
    regionNames = regionNames || new Intl.DisplayNames(["en"], { type: "region" });
    name = regionNames.of(upper) || text;
  } catch {
    name = upper;
  }
  countryNameCache.set(upper, name);
  return name;
};

/** Label for H-LINKs without a country / company. */
export const UNASSIGNED = "Unassigned";

export const isKoreaCountry = (name, code) =>
  String(code || "").trim().toUpperCase() === "KR" || /korea/i.test(String(name || ""));

/** A–Z with Unassigned last. */
export const compareNames = (a, b) => (a === UNASSIGNED) - (b === UNASSIGNED) || String(a).localeCompare(String(b));

/** Country order for site hierarchies: South Korea first, then A–Z, Unassigned last. */
export const compareCountries = (a, b, codeA, codeB) =>
  isKoreaCountry(b, codeB) - isKoreaCountry(a, codeA) || compareNames(a, b);

/**
 * Attach country + site + device name to IR trend rows using installation-info `sites`.
 * Prefers API fields when present; never uses HL-xx titles or location names as the main label.
 */
export const attachSiteDeviceLabels = (items, store) => {
  const index = buildSiteIndex(store);
  return (Array.isArray(items) ? items : []).map((item) => {
    const serial = String(item?.serialNumber || item?.serial_number || "").trim();
    const entry = serial ? index.get(serial) : null;
    const countryCode = String(
      item?.countryCode || item?.country_code || entry?.site?.country || ""
    )
      .trim()
      .toUpperCase();
    const countryLabel =
      String(item?.countryName || item?.country_name || item?.country || "").trim() ||
      countryName(countryCode) ||
      UNASSIGNED;
    const siteName =
      String(item?.siteName || item?.site_name || entry?.site?.name || "").trim() || UNASSIGNED;
    const deviceName =
      String(item?.deviceName || item?.device_name || entry?.device?.name || "").trim() ||
      serial ||
      "Device";
    return {
      ...item,
      countryCode,
      countryName: countryLabel,
      siteName,
      deviceName,
      serialNumber: serial || item?.serialNumber || "",
    };
  });
};

/** Group labeled IR trend items: country → site → devices (Korea first, Unassigned last). */
export const groupIrTrendByCountrySite = (items) => {
  const byCountry = new Map();
  (Array.isArray(items) ? items : []).forEach((item) => {
    const country = item?.countryName || UNASSIGNED;
    const code = item?.countryCode || "";
    const site = item?.siteName || UNASSIGNED;
    if (!byCountry.has(country)) {
      byCountry.set(country, { countryName: country, countryCode: code, sites: new Map() });
    }
    const countryGroup = byCountry.get(country);
    if (!countryGroup.countryCode && code) countryGroup.countryCode = code;
    if (!countryGroup.sites.has(site)) countryGroup.sites.set(site, []);
    countryGroup.sites.get(site).push(item);
  });

  return [...byCountry.values()]
    .sort((a, b) => compareCountries(a.countryName, b.countryName, a.countryCode, b.countryCode))
    .map((country) => ({
      countryName: country.countryName,
      countryCode: country.countryCode,
      sites: [...country.sites.entries()]
        .sort(([a], [b]) => compareNames(a, b))
        .map(([siteName, devices]) => ({
          siteName,
          devices: [...devices].sort((a, b) =>
            String(a.deviceName || "").localeCompare(String(b.deviceName || ""))
          ),
        })),
    }));
};

/** Flat site groups (legacy helper). */
export const groupIrTrendBySite = (items) =>
  groupIrTrendByCountrySite(items).flatMap((country) =>
    country.sites.map((site) => ({
      siteName: site.siteName,
      countryName: country.countryName,
      devices: site.devices,
    }))
  );

/** device_info keyed by H-LINK serial from a live sensor-data devices list. */
export const deviceInfoBySerial = (devices = []) =>
  new Map(
    devices
      .map((d) => [String(d?.device_info?.serial_number || ""), d?.device_info || {}])
      .filter(([serial]) => serial)
  );

/** Every site: all backend sites plus any saved/live H-LINK not yet assigned to one. */
export const listSites = (store, devices = []) => {
  const stored = store?.hlinks || {};
  const info = deviceInfoBySerial(devices);
  const index = buildSiteIndex(store);
  const names = (Array.isArray(store?.sites) ? store.sites : []).map((s) => s?.name).filter(Boolean);
  const serials = new Set([...Object.keys(stored), ...info.keys(), ...index.keys()].filter(Boolean));
  serials.forEach((serial) => {
    names.push(hlinkSiteName(serial, stored[serial], info.get(serial), index.get(serial)));
  });
  return [...new Set(names.filter(Boolean))];
};
